import _ from 'lodash';
import React, { useMemo } from 'react';

import { IImportSubmissionSummary, ImportRow } from '../../_core/Importer/importer.interface';
import { createStore } from '../../../lib/stores/create-store';
import { ParseError } from 'papaparse';
import moment from 'moment';

// Material UI
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';

// Data
import { STEPS } from '../../_core/Importer/importer.constants';
import { importTemplateUrl } from '../../_core/Importer/importer.helpers';
import { getImportSessionsMessages } from './import-sessions.messages';
import { replaceKeyWithID, toHash } from '../../../lib/helpers/data-structure.helpers';
import { timeToNum } from '../../../lib/helpers/conversion.helpers';
import { DEFAULT_SESSION_DURATION_MINUTES } from '../session.constants';

// Components
import { ImporterContainer } from '../../_core/Importer/importer.container';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useCompanySport, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { Sport, SportTerminology } from '../../../lib/types';
import { SPORT_TERMINOLOGY } from '../../../lib/constants';

const [useSessionImportStep, , setSessionImportStep] = createStore<number>(STEPS.INSTRUCTIONS);
const [useSessionImportItems, , setSessionImportItems] = createStore<ImportRow[]>([]);
const [useSessionImportErrors, , setSessionImportErrors] = createStore<ParseError[]>([]);

const facilityAddressAliases = Object.values(Sport).map((sport) => SPORT_TERMINOLOGY[sport][SportTerminology.FACILITY_ADDRESS])
const facilityAliases = Object.values(Sport).map((sport) => SPORT_TERMINOLOGY[sport][SportTerminology.FACILITY] + " Name")
const fieldAliases = Object.values(Sport).map((sport) => SPORT_TERMINOLOGY[sport][SportTerminology.FIELD] + " Name")

interface IImportSessionContainer {
  createAssessmentSessions: (rows: any[], summary: IImportSubmissionSummary, ageGroupsCreated: number) => void;
  createAgeGroup: Function;
  onImportError: (operation: string, error: unknown) => void;
  ageGroups: IAgeGroup[];
}

export const ImportSessionsContainer: React.FC<IImportSessionContainer> = ({
  createAssessmentSessions,
  createAgeGroup,
  onImportError,
  ageGroups
}) => {
  const sport = useCompanySport();
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getImportSessionsMessages(termLookup), [termLookup]);

  // Memoized: ImporterContainer folds these into expectedColumns, whose identity gates the
  // matcher's pre-map effect and ImporterResults' column effect.
  const requiredColumns = useMemo(() => [
    { key: 'address', label: termLookup[SportTerminology.FACILITY_ADDRESS], matchAliases: facilityAddressAliases },
    { key: 'date', label: 'Date', formatType: 'DATE', importFormat: 'YYYY/MM/DD', format: 'YYYY/MM/DD', validators: [
        (value: string, format?: string) => {
          const testValue = value.replace(/[\\,.\-:]/g,'/');
          let altFormat;

          switch(format) {
            case 'YYYY/MM/DD':
              altFormat = 'YYYY/M/D';
              break;
            case 'DD/MM/YYYY':
              altFormat = 'D/M/YYYY';
              break;
            case 'MM/DD/YYYY':
              altFormat = 'M/D/YYYY';
              break;
          }

          return moment(testValue, format, true).isValid() || moment(testValue, altFormat, true).isValid();
        }
      ] },
    { key: 'start', label: 'Start Time', matchAliases: ['Start'] },
    { key: 'ageGroup', label: 'Age Group' }
  ], [termLookup])

  const optionalColumns = useMemo(() => [
    { key: 'location', label: termLookup[SportTerminology.FACILITY] + ' Name', matchAliases: facilityAliases },
    { key: 'area', label: termLookup[SportTerminology.FIELD] + ' Name', matchAliases: fieldAliases},
    { key: 'duration', label: 'Duration (Min)', validators: [
        (value: string) => !_.isNaN(Number(value)),
        (value: string) => Number(value) > 4
    ] },
    { key: 'gender', label: 'Gender', matchAliases: ['GENDER (M, F)'] },
  ], [termLookup])

  const step = useSessionImportStep();
  const setStep = (step: number) => setSessionImportStep(step);

  const importItems = useSessionImportItems();
  const setImportItems = (items: ImportRow[]) => setSessionImportItems(items);
  const errorItems = useSessionImportErrors();
  const setImportErrors = (errors: ParseError[]) => setSessionImportErrors(errors);

  const handleSubmit = (rows: any[], summary: IImportSubmissionSummary) => {
    const currAgeGroupNameHash: { [key: string]: string } = toHash(ageGroups, 'name', '_id');
    let toCreate = replaceKeyWithID(rows, currAgeGroupNameHash, 'ageGroup', 'ageGroupId');

    rows.forEach(row => {
      row.start = timeToNum(row.start);
      // An unmapped Duration column leaves the row without one, and the wire rejects a
      // missing duration — so it falls back to the same default the create form seeds.
      row.duration = row.duration === undefined ? DEFAULT_SESSION_DURATION_MINUTES : Number(row.duration);
    });

    const createAgeGroups = toCreate.map(name => createAgeGroup(name))

    // Create any missing AgeGroups and assign them to the rows
    Promise.all(createAgeGroups).then(createdAgeGroupResults => {
      const importRows: any[] = [];
      const createdAgeGroups = createdAgeGroupResults.map(result => result.data?.createAgeGroup);
      const createdAgeGroupNameHash: { [key: string]: string } = toHash(createdAgeGroups, 'name', '_id');

      replaceKeyWithID(rows, createdAgeGroupNameHash, 'ageGroup', 'ageGroupId');

      rows.forEach(row => {
        importRows.push({
          ..._.omit(row, ['date']),
          date: row.date.replace(/[\\,.\-:]/g,'/')
        });
      })

      createAssessmentSessions(importRows, summary, _.compact(createdAgeGroups).length);
    }).catch((error: unknown) => onImportError('createAgeGroups', error));
  }

  return <ImporterContainer
    onSubmit={handleSubmit}
    step={step}
    setStep={setStep}
    importItems={importItems}
    setImportItems={setImportItems}
    importErrors={errorItems}
    setImportErrors={setImportErrors}
    requiredColumns={requiredColumns}
    optionalColumns={optionalColumns}
    extensions={['csv']}
    showFileStructure
    instructionHeader={<>
      <Typography variant="subtitle2" align="center">
        <IntlMsg {...messages.templatePrompt} values={{
          link: <Link href={importTemplateUrl('assessment-session', sport)} download={`${termLookup[SportTerminology.SESSION]} Import Template.csv`}>
            <IntlMsg {...messages.templateLink} />
          </Link>
        }} />
      </Typography>
    </>}
    instructionFooter={<Typography><IntlMsg {...messages.columnOrderNote} /></Typography>}
  />;
};
